import React, { useEffect, useState, useCallback } from 'react';
import { X, Plus, FileDown, Loader2 } from 'lucide-react';
import NewTransactionFormScreen from './NewTransactionFormScreen';
import { exportTransactionsPdf } from '../utils/exportTransactionsPdf';
import { useCurrency } from '../context/CurrencyContext';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost/ems/api';

const formatMoney = (amount, code = 'PKR') =>
  `${code} ${Number(amount || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
  })}`;

const TransactionHistoryModal = ({
  isOpen,
  onClose,
  transaction,
  projectId,
  projectName,
  canCreateTransactions = true,
  canExportTransactions = true,
  onTransactionAdded,
}) => {
  const { currency, fmt } = useCurrency();

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!transaction?.id) return;

    setLoading(true);

    try {
      const token = localStorage.getItem('ems_token');

      const response = await fetch(`${API_BASE}/transaction/${transaction.id}/history`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });

      const result = await response.json();

      if (result?.success) {
        setHistory(Array.isArray(result.data) ? result.data : []);
      } else {
        setHistory([]);
      }
    } catch (error) {
      console.error('History fetch failed:', error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [transaction?.id]);

  useEffect(() => {
    if (isOpen && transaction?.id) {
      fetchHistory();
    }
  }, [isOpen, transaction?.id, fetchHistory]);

  if (!isOpen) return null;

  const handleSaved = async () => {
    setAddModalOpen(false);
    await onTransactionAdded?.();
    await fetchHistory();
  };

  const handleExportPDF = async () => {
    setExporting(true);

    try {
      const rows = [
        transaction,
        ...history.map((item) => ({
          id: item.id,
          date: item.created_at,
          project_name: projectName,
          category_name: item.after?.category_name || transaction?.category_name,
          description: `${item.action || 'history'} - ${
            item.after?.description || transaction?.description || ''
          }`,
          type: item.after?.type || transaction?.type,
          amount: item.after?.amount || transaction?.amount,
          currency: item.after?.currency || transaction?.currency || 'PKR',
          running_balance:
            item.after?.running_balance || transaction?.running_balance || 0,
        })),
      ].filter(Boolean);

      exportTransactionsPdf({
        rows,
        currency,
        fmt,
        title: 'Transaction History',
        filePrefix: 'transaction-history',
        filterSummary: `Project: ${projectName || 'N/A'} · Transaction ID: ${
          transaction?.id || 'N/A'
        }`,
      });
    } catch (error) {
      console.error('History PDF export failed:', error);
      alert('PDF export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
        <div className="w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl bg-white shadow-2xl border border-slate-200">
          <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-slate-100">
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                Transaction History
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                {projectName || 'Selected Project'} · Transaction #{transaction?.id}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-slate-100 transition"
            >
              <X size={20} />
            </button>
          </div>

          <div className="overflow-y-auto max-h-[calc(90vh-75px)] p-5">

              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-sm font-bold text-slate-800">
                  History List
                </h3>
                <p className="text-xs text-slate-500">
                  Created / updated / deleted snapshots will show below.
                </p>
              </div>

              <div className="flex items-center gap-2">
                {canExportTransactions ? (
                  <button
                    type="button"
                    onClick={handleExportPDF}
                    disabled={exporting}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold disabled:opacity-50"
                    style={{
                      background: 'rgba(196,69,105,0.08)',
                      color: '#c44569',
                      border: '1.5px solid #f9b8ca',
                    }}
                  >
                    {exporting ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <FileDown size={14} />
                        Export PDF
                      </>
                    )}
                  </button>
                ) : null}

                {canCreateTransactions ? (
                  <button
                    type="button"
                    onClick={() => setAddModalOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold text-white"
                    style={{ background: '#c44569' }}
                  >
                    <Plus size={14} />
                    Add New Transaction
                  </button>
                ) : null}
              </div>
            </div>
            <div
              className="rounded-2xl border p-4 mb-5"
              style={{
                borderColor: '#f9b8ca',
                background: '#fff6f8',
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-slate-500">Date</p>
                  <p className="text-sm font-bold text-slate-800">
                    {transaction?.date || '—'}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-slate-500">Description</p>
                  <p className="text-sm font-bold text-slate-800 truncate">
                    {transaction?.description || '—'}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-slate-500">Type</p>
                  <p className="text-sm font-bold text-slate-800 capitalize">
                    {transaction?.type || '—'}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-slate-500">Amount</p>
                  <p
                    className="text-sm font-bold"
                    style={{
                      color:
                        transaction?.type === 'receipt' ? '#b08800' : '#c44569',
                    }}
                  >
                    {formatMoney(transaction?.amount, transaction?.currency || 'PKR')}
                  </p>
                </div>
              </div>
            </div>

          

            {loading ? (
              <div className="flex items-center justify-center py-12 gap-3 text-slate-400">
                <Loader2 className="animate-spin h-5 w-5" />
                <span className="text-sm">Loading history...</span>
              </div>
            ) : history.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center">
                <p className="text-sm text-slate-500">
                  No history found for this transaction.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((item, index) => {
                  const after = item.after || {};
                  const before = item.before || {};

                  return (
                    <div
                      key={item.id || index}
                      className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                        <div>
                          <p className="text-sm font-bold text-slate-800 capitalize">
                            {item.action || 'History'}
                          </p>
                          <p className="text-xs text-slate-500">
                            {item.created_at || '—'}
                          </p>
                        </div>

                        <span
                          className="px-3 py-1 rounded-full text-xs font-semibold capitalize"
                          style={{
                            background:
                              item.action === 'created'
                                ? '#f0fdf4'
                                : item.action === 'updated'
                                  ? '#eef2ff'
                                  : '#fff6f8',
                            color:
                              item.action === 'created'
                                ? '#16a34a'
                                : item.action === 'updated'
                                  ? '#4338ca'
                                  : '#c44569',
                          }}
                        >
                          {item.action || 'history'}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                        <div className="rounded-xl bg-slate-50 p-3">
                          <p className="text-xs text-slate-500">Description</p>
                          <p className="font-semibold text-slate-800 truncate">
                            {after.description ||
                              before.description ||
                              transaction?.description ||
                              '—'}
                          </p>
                        </div>

                        <div className="rounded-xl bg-slate-50 p-3">
                          <p className="text-xs text-slate-500">Type</p>
                          <p className="font-semibold text-slate-800 capitalize">
                            {after.type || before.type || transaction?.type || '—'}
                          </p>
                        </div>

                        <div className="rounded-xl bg-slate-50 p-3">
                          <p className="text-xs text-slate-500">Amount</p>
                          <p className="font-semibold text-slate-800">
                            {formatMoney(
                              after.amount || before.amount || transaction?.amount,
                              after.currency ||
                                before.currency ||
                                transaction?.currency ||
                                'PKR'
                            )}
                          </p>
                        </div>

                        <div className="rounded-xl bg-slate-50 p-3">
                          <p className="text-xs text-slate-500">Balance</p>
                          <p className="font-semibold text-slate-800">
                            {formatMoney(
                              after.running_balance ||
                                before.running_balance ||
                                transaction?.running_balance,
                              after.currency ||
                                before.currency ||
                                transaction?.currency ||
                                'PKR'
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <NewTransactionFormScreen
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSaved={handleSaved}
        editData={null}
        initialProjectId={projectId}
        initialProjectName={projectName}
        lockProject={Boolean(projectId)}
      />
    </>
  );
};

export default TransactionHistoryModal;